import { OfferService, Slot, SlotService } from '../../../../../../reservation.model';
import { ServiceCountingMethods, ServiceDependsOn } from '@/services/service.model';

/**
 * Display name of the cancellation insurance service type.
 * Must be priced last, after all other slot services.
 */
export const RESERVATION_CANCELLATION_INSURANCE_NAME = 'Ubezpieczenie od rezygnacji';

/**
 * Returns the slot price base used for percentage-based services (in grosze).
 * Includes catalog price, promotion, and board; excludes transport.
 *
 * @param slot - Reservation slot with pricing fields.
 * @returns Price base in grosze.
 */
export function getReservationServiceSlotPrice(slot: Slot): number {
	return slot.catalogPrice - slot.promotionValue + slot.priceBoard;
}

/**
 * Returns the sum of all slot service prices (in grosze).
 *
 * @param slot - Reservation slot with {@link Slot.slotServices}.
 * @returns Total price of slot services in grosze.
 */
export function getReservationSlotServicesSum(slot: Slot): number {
	return (slot.slotServices ?? []).reduce((accumulator, slotService) => accumulator + slotService.price, 0);
}

/**
 * Returns the participant total price (in grosze), including accommodation and transport,
 * with the current sum of services and price adjustment applied on top of the API {@link Slot.totalPrice}.
 *
 * @param slot - Reservation slot with {@link Slot.totalPrice}, services, and {@link Slot.priceAdjustment}.
 * @param initialServicesSum - Sum of service prices when the dialog was opened (in grosze).
 * @param initialPriceAdjustment - Price adjustment when the dialog was opened (in grosze).
 * @returns Total price for the participant in grosze.
 */
export function getReservationSlotTotalPrice(
	slot: Slot,
	initialServicesSum: number,
	initialPriceAdjustment = 0,
): number {
	return (
		slot.totalPrice -
		initialServicesSum -
		initialPriceAdjustment +
		getReservationSlotServicesSum(slot) +
		(slot.priceAdjustment ?? 0)
	);
}

/**
 * Returns the number of nights used as a multiplier for {@link ServiceDependsOn.NIGHTS_STAY} services.
 * Fallback order: reservation nights → slot nights → slot days.
 *
 * @param slot - Reservation slot.
 * @param reservationNightsStay - Nights stay from the reservation, when available.
 * @returns Number of nights for pricing.
 */
export function getReservationSlotNightsStay(slot: Slot, reservationNightsStay?: number): number {
	const slotWithNightsStay = slot as Slot & { nightsStay?: number };
	return reservationNightsStay ?? slotWithNightsStay.nightsStay ?? slot.days;
}

/**
 * Recalculates prices for all services on a slot and writes them to {@link SlotService.price}.
 * Cancellation insurance is calculated last, after other services.
 *
 * @param slot - Slot whose services are recalculated (mutated in place).
 * @param reservationNightsStay - Nights stay from the reservation, when available.
 * @returns void
 */
export function recalculateReservationSlotServicesPrices(slot: Slot, reservationNightsStay?: number): void {
	moveCancellationInsuranceToTheEndOfArray(slot.slotServices, () => {
		slot.slotServices.forEach((slotService) => {
			slotService.price = calculateReservationServicePrice(slotService.service, slot, reservationNightsStay);
		});
	});
}

/**
 * Calculates the price of a single service for a slot (in grosze).
 * Applies counting method (fixed, permille, custom) and dependency (slot, day, nights stay).
 *
 * @param service - Offer service definition with method and dependency.
 * @param slot - Reservation slot the service belongs to.
 * @param reservationNightsStay - Nights stay from the reservation, when available.
 * @returns Calculated service price in grosze.
 * @throws {Error} When counting method or dependency is not supported.
 */
export function calculateReservationServicePrice(
	service: OfferService,
	slot: Slot,
	reservationNightsStay?: number,
): number {
	let serviceValue = 0;
	let serviceMultiplier = 1;
	const slotPrice = getReservationServiceSlotPrice(slot);

	switch (service.countingMethod) {
		case ServiceCountingMethods.CUSTOM:
			serviceValue = getServiceValueFromMethod(
				slotPrice + sumOfServicesExcludingCancellationInsurance(slot.slotServices),
				service.methodValue,
			);
			break;
		case ServiceCountingMethods.VALUE:
			serviceValue = service.methodValue;
			break;
		case ServiceCountingMethods.PERMILLE:
			serviceValue = getServiceValueFromMethod(slotPrice, service.methodValue);
			break;
		default:
			throw new Error(`Unsupported counting method: ${service.countingMethod}`);
	}

	switch (service.dependsOn) {
		case ServiceDependsOn.SLOT:
			serviceMultiplier = 1;
			break;
		case ServiceDependsOn.DAY:
			serviceMultiplier = slot.days;
			break;
		case ServiceDependsOn.NIGHTS_STAY:
			serviceMultiplier = getReservationSlotNightsStay(slot, reservationNightsStay);
			break;
		default:
			throw new Error(`Unsupported service dependency: ${service.dependsOn}`);
	}

	return serviceMultiplier * serviceValue;
}

/**
 * Converts a base value and method value to a service amount: `floor(value * methodValue / 10000)`.
 *
 * @param value - Base amount in grosze.
 * @param methodValue - Method value in permille (e.g. -500 = -5%).
 * @returns Computed amount in grosze.
 */
function getServiceValueFromMethod(value: number, methodValue: number): number {
	return Math.floor((value * methodValue) / 10000);
}

/**
 * Sums slot service prices excluding cancellation insurance.
 * Used as part of the base for {@link ServiceCountingMethods.CUSTOM} pricing.
 *
 * @param slotServices - Services attached to the slot.
 * @returns Sum of prices in grosze, excluding cancellation insurance.
 */
function sumOfServicesExcludingCancellationInsurance(slotServices: SlotService[]): number {
	return slotServices
		.filter((slotService) => slotService.service?.serviceType?.name !== RESERVATION_CANCELLATION_INSURANCE_NAME)
		.reduce((accumulator, slotService) => accumulator + slotService.price, 0);
}

/**
 * Temporarily moves cancellation insurance to the end of the array for the duration of `callback`,
 * so it is priced after other services, then restores the original order.
 *
 * @param slotServices - Mutable list of slot services.
 * @param callback - Function that runs while cancellation insurance is at the end.
 * @returns void
 */
function moveCancellationInsuranceToTheEndOfArray(slotServices: SlotService[], callback: () => void): void {
	const cancellationInsuranceIndex = slotServices.findIndex(
		(slotService) => slotService.service?.serviceType?.name === RESERVATION_CANCELLATION_INSURANCE_NAME,
	);
	if (cancellationInsuranceIndex !== -1) {
		slotServices.push(slotServices.splice(cancellationInsuranceIndex, 1)[0]);
	}
	callback();
	if (cancellationInsuranceIndex !== -1) {
		slotServices.splice(cancellationInsuranceIndex, 0, slotServices.pop()!);
	}
}
